import { useCallback, useMemo, useRef } from 'react'
import { useDraftState } from '@papan/core-ui/ui'

/**
 * Undo/redo for the database grid's STAGED changes — the ones that have not
 * been sent anywhere yet.
 *
 * This grid stages what the user does: marking rows for deletion, adding a
 * row. Nothing reaches the database until Save. Undo therefore never talks to
 * a server, never reads a value back, and cannot disturb anybody else's work —
 * it moves local state and nothing more. A .db here can be the live database
 * behind a PDApp people are using right now, and that is exactly why the undo
 * for it has to stop at the boundary of what has been sent.
 *
 * WHAT IT DOES NOT COVER, deliberately:
 *
 *   - Text inside a cell. Those are real <textarea>s and the browser's own
 *     undo works in them while the caret is there; the global Ctrl+Z listener
 *     stands aside for inputs precisely so it keeps working. Taking that over
 *     would replace a per-keystroke history with a coarser one.
 *   - Anything already saved. A saved write is final in this grid, as it is in
 *     DBeaver, DataGrip and TablePlus. Version history is the way back.
 *
 * SNAPSHOTS, NOT INVERSES. The staged set is small — a set of row ids and a
 * short list of new rows — so each step stores the whole thing and undo puts
 * the previous one back. The alternative, recording an inverse per action,
 * is where the SAVED-change undo went wrong: every inverse needed to be
 * correct on its own, and they compounded. There is nothing to get subtly
 * wrong about restoring a copy.
 */

/** One staged new row. `tempId` stands in for the rowid the database has not
 *  assigned yet; it only has to be unique on screen. */
export interface StagedInsert {
  tempId: string
  values: Record<string, unknown>
  functions?: Record<string, string>
}

/** Everything the grid holds that has not been sent. */
export interface StagedState {
  deleteRowIds: Set<number>
  inserts: StagedInsert[]
}

export interface GridEditsHandle {
  deleteRowIds: Set<number>
  inserts: StagedInsert[]
  /** Replace the staged state and push the previous one onto the undo stack.
   *  Takes the current state so a caller can compute from it, exactly like a
   *  React updater. */
  stage: (next: (current: StagedState) => StagedState) => void
  /** Drop everything staged AND the history — Discard.
   *  Not undoable: it is itself the "take it all back" action. */
  reset: () => void
  /**
   * Declare what the staged set belongs to — file, version, table.
   *
   * Resets when the key actually CHANGES, which is not the same as "when the
   * grid mounts". The grid remounts on a collab reconnect, and an effect that
   * reset on mount threw the staged set away every time the socket blinked —
   * which is what hoisting this hook out of the grid was meant to stop.
   * Comparing against the key the hook is already holding cannot be fooled
   * that way.
   *
   * Returns whether it actually reset, so the caller can tell the user their
   * staged work went — and stay quiet on the remount, where it did not.
   */
  scopeTo: (key: string) => boolean
  /**
   * The staged set belongs to a NEW key, unchanged — a rename.
   *
   * Renaming a table changes what it is called and nothing else: the same
   * rows, the same rowids, the same columns. Letting that reach `scopeTo`
   * would read as a move to a different table and throw away everything
   * staged, so the marks and the new rows the user was about to save would
   * vanish for a rename they asked for.
   */
  rekey: (key: string) => void
  /** A unique id for a row the database has not numbered yet. Lives here so it
   *  cannot be reset independently of the inserts that carry it. */
  nextTempId: () => string
  /**
   * Forget specific staged items ENTIRELY — from the current state and from
   * every snapshot in the history.
   *
   * Two callers, one reason. A Save passes what it SENT: those changes are in
   * the database now, and a snapshot that still carries them would let one
   * Ctrl+Z re-stage applied work — a saved insert undone and saved again is a
   * duplicate row. A refresh passes rows that have VANISHED: a mark for a row
   * that is gone is a delete of whatever rowid SQLite hands out next, which
   * can be a colleague's new row.
   *
   * Purging rather than clearing is the point. Everything else the user staged
   * is still local, still theirs, and still undoable.
   */
  purge: (gone: { deleteRowIds?: Iterable<number>; insertTempIds?: Iterable<string> }) => void
  undo: () => boolean
  redo: () => boolean
  canUndo: boolean
  canRedo: boolean
}

const EMPTY: StagedState = { deleteRowIds: new Set(), inserts: [] }

const copy = (s: StagedState): StagedState => ({
  deleteRowIds: new Set(s.deleteRowIds),
  inserts: [...s.inserts],
})

const same = (a: StagedState, b: StagedState): boolean =>
  a.deleteRowIds.size === b.deleteRowIds.size
  && [...a.deleteRowIds].every(id => b.deleteRowIds.has(id))
  && a.inserts.length === b.inserts.length
  && a.inserts.every((ins, i) => ins.tempId === b.inserts[i]?.tempId)

/** Drop steps that no longer change anything.
 *
 *  Purging can flatten a run of snapshots into identical ones — mark two rows,
 *  save both, and every step in between now describes the same empty set. Left
 *  alone the Edit menu offers an Undo that visibly does nothing, which is the
 *  one thing the menu must never do. */
const collapse = (stack: StagedState[], current: StagedState): StagedState[] => {
  const out: StagedState[] = []
  let prev = current
  for (const entry of stack) {
    if (!same(entry, prev)) out.push(entry)
    prev = entry
  }
  return out
}

/** Cap the history so a long editing session cannot grow it without bound.
 *  Each entry is a set of numbers and a handful of small objects, so this is
 *  generous; it exists to have a bound at all, not because 50 is a limit
 *  anyone reaches by hand. */
const MAX_HISTORY = 50

/** Everything the hook holds, as ONE value — so it can live in the draft store
 *  and survive a phone/desktop layout swap (#897). `scope` and `nextTemp` ride
 *  along: a restored set must stay bound to the table it was staged for, and a
 *  restored insert's temp id must not be handed out again. */
interface EditsDraft {
  staged: StagedState
  undoStack: StagedState[]
  redoStack: StagedState[]
  scope: string | null
  nextTemp: number
}

const INITIAL: EditsDraft = { staged: EMPTY, undoStack: [], redoStack: [], scope: null, nextTemp: 1 }

/**
 * `draftKey` (#897): name the editor's place (`db:<scope>:<file>`) and the staged
 * set, its history and its scope survive the editor itself unmounting in a
 * layout swap (`useDraftState`). `null` — local state, as before.
 */
export function useGridEdits(draftKey: string | null = null): GridEditsHandle {
  const [draft, setDraft, { clear }] = useDraftState<EditsDraft>(draftKey, INITIAL)
  /** The current value, assigned SYNCHRONOUSLY by `write`: two calls in the
   *  same tick both read it, and the second would otherwise compute from the
   *  state before the first. */
  const draftRef = useRef(draft)
  draftRef.current = draft
  const scopeRef = useRef<string | null>(draft.scope)
  const tempIdRef = useRef(draft.nextTemp)

  const write = useCallback((next: Omit<EditsDraft, 'scope' | 'nextTemp'>) => {
    const full: EditsDraft = { ...next, scope: scopeRef.current, nextTemp: tempIdRef.current }
    draftRef.current = full
    setDraft(full)
  }, [setDraft])

  const stage = useCallback((next: (current: StagedState) => StagedState) => {
    const d = draftRef.current
    const before = copy(d.staged)
    // A new action makes the redo stack describe a future that no longer
    // follows from here — the same rule every editor uses.
    write({ staged: next(d.staged), undoStack: [before, ...d.undoStack].slice(0, MAX_HISTORY), redoStack: [] })
  }, [write])

  const purge = useCallback((gone: {
    deleteRowIds?: Iterable<number>
    insertTempIds?: Iterable<string>
  }) => {
    const ids = new Set(gone.deleteRowIds ?? [])
    const temps = new Set(gone.insertTempIds ?? [])
    if (ids.size === 0 && temps.size === 0) return

    const strip = (s: StagedState): StagedState => ({
      deleteRowIds: ids.size === 0 ? s.deleteRowIds
        : new Set([...s.deleteRowIds].filter(id => !ids.has(id))),
      inserts: temps.size === 0 ? s.inserts
        : s.inserts.filter(i => !temps.has(i.tempId)),
    })

    const d = draftRef.current
    const next = strip(d.staged)
    // The snapshots too — a history entry is a state the user could go back
    // to, and one carrying an applied or vanished item is a state they must
    // not. Steps that stop changing anything are dropped, so the menu keeps
    // telling the truth about what Ctrl+Z would do.
    write({
      staged: next,
      undoStack: collapse(d.undoStack.map(strip), next),
      redoStack: collapse(d.redoStack.map(strip), next),
    })
  }, [write])

  const reset = useCallback(() => {
    draftRef.current = { ...INITIAL, scope: scopeRef.current, nextTemp: tempIdRef.current }
    // Nothing left to keep: the draft entry itself goes (the scope stays here).
    clear()
  }, [clear])

  /** True when there is anything a reset would throw away — rows staged now,
   *  or a history the user could still walk back through. */
  const hasWork = useCallback(() => {
    const d = draftRef.current
    return d.staged.deleteRowIds.size > 0
      || d.staged.inserts.length > 0
      || d.undoStack.length > 0
      || d.redoStack.length > 0
  }, [])

  const scopeTo = useCallback((key: string): boolean => {
    if (scopeRef.current === key) return false
    const first = scopeRef.current === null
    scopeRef.current = key
    // Nothing can be staged before the first scope is declared, so that call
    // has nothing to clear — and clearing anyway would undo the hoist.
    if (first) return false
    // Includes the HISTORY, not just what is staged now. Undo everything back
    // to empty and the stacks still hold work the user could walk forward
    // into; a reset destroys that too, so saying nothing was discarded would
    // be untrue.
    const had = hasWork()
    reset()
    return had
  }, [reset, hasWork])

  const rekey = useCallback((key: string) => {
    scopeRef.current = key
    // A kept draft must follow the rename, or a restore would read it as
    // belonging to the old name and drop it.
    if (hasWork()) {
      const d = draftRef.current
      write({ staged: d.staged, undoStack: d.undoStack, redoStack: d.redoStack })
    }
  }, [hasWork, write])

  const nextTempId = useCallback(() => `new_${tempIdRef.current++}`, [])

  // The snapshot is taken EAGERLY, from the ref, before anything is written —
  // reading state inside a deferred updater gave the wrong side of the step.
  const undo = useCallback((): boolean => {
    const d = draftRef.current
    const top = d.undoStack[0]
    if (!top) return false
    write({ staged: top, undoStack: d.undoStack.slice(1), redoStack: [copy(d.staged), ...d.redoStack].slice(0, MAX_HISTORY) })
    return true
  }, [write])

  const redo = useCallback((): boolean => {
    const d = draftRef.current
    const top = d.redoStack[0]
    if (!top) return false
    write({ staged: top, undoStack: [copy(d.staged), ...d.undoStack].slice(0, MAX_HISTORY), redoStack: d.redoStack.slice(1) })
    return true
  }, [write])

  const staged = draft.staged
  const undoStack = draft.undoStack
  const redoStack = draft.redoStack
  return useMemo(() => ({
    deleteRowIds: staged.deleteRowIds,
    inserts: staged.inserts,
    stage,
    purge,
    reset,
    scopeTo,
    rekey,
    nextTempId,
    undo,
    redo,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
  }), [staged, stage, purge, reset, scopeTo, rekey, nextTempId, undo, redo, undoStack.length, redoStack.length])
}
